import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Card from '../ui/Card';
import { StepIcon } from './stepIcons';

// Checklist persistent pe Dashboard — apare pentru userii care au sărit pași în wizard
// SAU au pași noi introduși după ce au făcut onboarding-ul (introducedAt > completedAt).
//
// Filozofie:
//  - Nu blocăm: utilizatorul face pașii când vrea
//  - Dispare singur când totul e completat
//  - Click pe „Du-mă acolo" → navighează + marchează seen automat (cu o întârziere mică ca să se
//    încarce pagina; dacă userul nu face acțiunea reală, va re-apărea în checklist data viitoare —
//    asta e OK, nu vrem să-l ascundem prematur)
//  - „Marchează ca rezolvat" = manual seen (când userul a făcut deja acțiunea în alt context)

const OnboardingChecklist = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pornește RESTRÂNS — doar antetul (titlu + progres) e vizibil; userul extinde dacă vrea.
  // Cerință owner: după închiderea wizardului + redirect pe dashboard, meniul să nu fie extins.
  const [collapsed, setCollapsed] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    api
      .get('/onboarding/progress')
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Reîmprospătează checklist-ul când wizardul se închide (skip-all sau finish).
  // Wizardul emite `onboarding:closed` din Layout, ca să nu fie nevoie de refresh manual.
  useEffect(() => {
    // La închiderea wizardului (skip-all/finish) → reîmprospătează ȘI restrânge meniul, ca pe
    // dashboard să nu apară extins imediat după ieșirea din wizard.
    const handler = () => {
      setCollapsed(true);
      reload();
    };
    window.addEventListener('onboarding:closed', handler);
    return () => window.removeEventListener('onboarding:closed', handler);
  }, [reload]);

  if (loading) return null;
  if (!data) return null;

  // Afișăm checklist-ul DOAR când userul a finalizat wizardul (completedAt setat)
  // ȘI mai are pași netraversați. Dacă încă nu a făcut wizardul, Layout afișează modalul.
  if (!data.completedAt) return null;

  const remaining = (data.steps || []).filter((s) => !s.seen);
  if (remaining.length === 0) return null;

  const total = data.steps.length;
  const done = data.seenCount;

  const markSeen = async (stepId) => {
    try {
      await api.post('/onboarding/seen-step', { stepId });
    } catch (_) {
      /* best-effort */
    }
    reload();
  };

  const goTo = async (step) => {
    await markSeen(step.id);
    if (step.navigateTo) navigate(step.navigateTo);
  };

  return (
    <Card className="border-primary-200 dark:border-primary-800/60 bg-primary-50/40 dark:bg-primary-950/20">
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-start gap-2 text-left flex-1 min-w-0"
        >
          <svg
            className={`w-5 h-5 mt-0.5 text-slate-400 dark:text-slate-400 transition-transform flex-shrink-0 ${collapsed ? '' : 'rotate-90'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {t('onboarding.checklist.title')}
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              {t('onboarding.checklist.doneCount', { done, total })} ·{' '}
              {t('onboarding.checklist.subtitle')}
            </p>
          </div>
        </button>
        {/* Mini progress bar */}
        <div className="w-24 flex-shrink-0 mt-1.5">
          <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 transition-all duration-300"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="space-y-2">
          {remaining.map((step) => (
            <div
              key={step.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:shadow-sm transition"
            >
              <div className="flex-shrink-0">
                <StepIcon stepId={step.id} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {t(step.titleKey)}
                  </p>
                  {step.isNew && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      {t('onboarding.checklist.newBadge')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                  {t(step.descKey)}
                </p>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                {step.navigateTo && (
                  <button
                    onClick={() => goTo(step)}
                    className="text-xs text-primary-600 dark:text-primary-300 hover:underline whitespace-nowrap font-medium"
                  >
                    {t('onboarding.checklist.goNow')} →
                  </button>
                )}
                <button
                  onClick={() => markSeen(step.id)}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:underline whitespace-nowrap"
                >
                  {t('onboarding.checklist.markDone')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Filtrul pentru container-ul mini (mai compact). În mod normal e identic cu cel mare,
// pe viitor putem face un design diferit dacă e nevoie. Pentru moment folosim aceeași componentă.
// (Această funcționalitate e exportată separat pentru flexibilitate viitoare.)
export const compactChecklist = false;

export default OnboardingChecklist;
